import React from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { heroData, aboutData, galleryData, servicesData } from '../../mocks/homepage';

const HomePage: React.FC = () => {
  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=professional%20garage%20door%20installation%20company%20hero%20background%20modern%20residential%20house%20with%20elegant%20sectional%20garage%20doors%20sunset%20golden%20hour%20lighting%20beautiful%20craftsmanship%20quality%20workmanship%20architectural%20excellence&width=1920&height=1080&seq=4&orientation=landscape')`
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 container mx-auto px-6 text-center text-white">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block bg-red-600/20 backdrop-blur-sm border border-red-500/30 rounded-full px-6 py-2 mb-8">
              <span className="text-red-200 font-medium">{heroData.badge}</span>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold font-serif mb-6">
              {heroData.title}<br />
              <span className="text-red-400">{heroData.subtitle}</span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-200 mb-12 max-w-3xl mx-auto leading-relaxed">
              {heroData.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                variant="primary" 
                size="lg"
                icon="ri-phone-line"
                href="tel:+381600706072"
              >
                Pozovite Nas Sada
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                to="/galerija"
                icon="ri-image-line"
              >
                Pogledajte Radove
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block bg-red-600/20 rounded-lg px-4 py-2 mb-6">
                <span className="text-red-400 font-medium">O Nama</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight">
                {aboutData.title}
              </h2>
              
              <p className="text-xl text-gray-300 mb-12 leading-relaxed">
                {aboutData.description}
              </p>
              
              <div className="flex items-center gap-8">
                <div>
                  <div className="text-6xl font-bold text-white font-serif mb-2">
                    {aboutData.projects}
                  </div>
                  <div className="text-gray-400">
                    {aboutData.projectsLabel}
                  </div>
                </div>
                <Button variant="outline" to="/kontakt">
                  Saznajte Više
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20garage%20door%20installation%20team%20working%20on%20sectional%20door%20high%20quality%20craftsmanship%20experienced%20workers%20modern%20tools%20professional%20service%20quality%20installation%20process&width=600&height=500&seq=5&orientation=landscape"
                alt="Profesionalni tim na radu"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mini Gallery Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl lg:text-6xl font-bold font-serif text-gray-900 mb-8">
              Naši<br />
              Realizovani Projekti
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {galleryData.map((project) => (
              <div key={project.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="h-64 bg-white p-4">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover rounded-xl shadow-sm"
                  />
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <Button variant="outline" to="/galerija" icon="ri-arrow-right-line">
              Pogledajte Kompletnu Galeriju
            </Button>
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Kompletan Spektar{' '}
              <span className="text-red-600">Profesionalnih Usluga</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Large Card */}
            <div className="lg:row-span-2 relative rounded-2xl overflow-hidden shadow-xl h-96 lg:h-full">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20garage%20door%20measurement%20and%20consultation%20service%20expert%20technician%20measuring%20garage%20opening%20quality%20assessment%20free%20estimate%20professional%20consultation&width=600&height=800&seq=6&orientation=portrait"
                alt="Besplatna procena"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute bottom-6 left-6">
                <div className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold">
                  Besplatna Procena
                </div>
              </div>
            </div>
            
            {/* Two smaller cards */}
            <div className="grid grid-cols-1 gap-8">
              <div className="relative rounded-2xl overflow-hidden shadow-xl h-44">
                <img
                  src="https://readdy.ai/api/search-image?query=garage%20door%20professional%20installation%20process%20sectional%20door%20mounting%20high%20quality%20workmanship%20expert%20technicians%20modern%20garage%20professional%20service&width=600&height=300&seq=7&orientation=landscape"
                  alt="Profesionalna ugradnja"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-700/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="text-white font-semibold">Ugradnja</div>
                </div>
              </div>
              
              <div className="relative rounded-2xl overflow-hidden shadow-xl h-44">
                <img
                  src="https://readdy.ai/api/search-image?query=garage%20door%20maintenance%20and%20service%20professional%20technician%20repairing%20sectional%20door%20quality%20service%20tools%20professional%20maintenance%20work&width=600&height=300&seq=8&orientation=landscape"
                  alt="Servis i održavanje"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-pink-600/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="text-white font-semibold">Servisiranje</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="relative py-32">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=satisfied%20customer%20standing%20in%20front%20of%20newly%20installed%20garage%20door%20beautiful%20residential%20house%20professional%20installation%20quality%20workmanship%20happy%20homeowner%20modern%20sectional%20door&width=1920&height=800&seq=9&orientation=landscape')`
          }}
        ></div>
        <div className="absolute inset-0 bg-black/50"></div>
        
        <div className="relative z-10 container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
            <div className="lg:col-span-1">
              <h2 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
                Spremni za nova vrata?
              </h2>
            </div>
            
            <div className="lg:col-span-1 lg:text-right">
              <p className="text-xl text-white font-light leading-relaxed">
                Kontaktirajte nas danas za<br />
                besplatnu procenu i ponudu
              </p>
            </div>
            
            <div className="lg:col-span-1">
              <Button 
                variant="primary" 
                size="lg"
                href="tel:+381600706072"
                className="w-full lg:w-auto bg-red-600 text-white hover:bg-red-700 shadow-2xl"
                icon="ri-arrow-right-line"
              >
                Kontaktirajte Nas
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;